import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
    CheckboxItem,
    CheckboxLabel,
    StyledLabel,
    DropdownChevron

} from '../StyledComponents/UserFormStyledComponents';
import { AppColors } from '../StyledComponents/Graphics';

const EnhancedCheckboxListWrapper = styled.div`
    width: 100%;
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 8px 12px;
    border: 1px solid ${AppColors.MRNAVY60};
    background-color: transparent;
    color: ${AppColors.MRNAVY60};
    font-size: 14px;
        
    &::placeholder {
        color: ${AppColors.MRNAVY60};
    }
`;

const DropdownHeader = styled.div`
    padding: 10px 12px;
    border: 1px solid ${AppColors.MRNAVY60};
    border-radius: ${props => props.isOpen ? '2px 2px 0 0' : '2px'};
    background-color: transparent;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${props => props.isOpen ? '0' : '0'};
    font-size:14px;
    
    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
        border-color: ${AppColors.MRBLUE};
    }
`;


const ScrollableContainer = styled.div`
    max-height: ${props => props.maxHeight || '300px'};
    overflow-y: auto;
    border-radius: 0 0 4px 4px;
    border: 1px solid ${AppColors.MRNAVY60};
    border-top: none;
    padding: 0px;
    background-color: transparent;
    
    &::-webkit-scrollbar {
        width: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: ${AppColors.MRNAVY60};
        border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
        border-radius: 4px;
        
        &:hover {
            background: ${AppColors.MRNAVY60};
        }
    }
    
    scrollbar-width: thin;
    scrollbar-color: ${AppColors.MRNAVY60} ${AppColors.MRNAVY};
`;





const EnhancedCheckboxList = ({
    handleChange,
    id,
    label,
    options,
    value,
    maxVisibleItems = 6,
    enableRightClick = true,
    showTooltips = true,
    enableSearch = true
}) => {
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [lastRightClickedOption, setLastRightClickedOption] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(true);
    const [hoveredOption, setHoveredOption] = useState(null);
    const [isHoveringCheckbox, setIsHoveringCheckbox] = useState(false);

    const maxHeight = `${maxVisibleItems * 30}px`;

    useEffect(() => {
        if (value === 'Any' || value === '') {
            setSelectedOptions(options || []);
            setLastRightClickedOption(null);
        } else if (value.includes(' AND NOT (')) {
            // Parse the value to extract included options
            // Format: (opt1) OR (opt2) AND NOT (dtm_summary->'dtm_summary_meta'->'tags' ? ANY(ARRAY[...]))
            const parts = value.split(' AND NOT (');
            const includedPart = parts[0];
            
            const includedValues = [];
            const optionMatches = includedPart.match(/\([^)]+\)/g);
            
            if (optionMatches) {
                optionMatches.forEach(match => {
                    const cleanValue = match.slice(1, -1); 
                    includedValues.push(cleanValue);
                });
            }
            
            const matchedOptions = options.filter(opt => 
                includedValues.some(val => 
                    opt.value === val || 
                    val.includes(opt.value) || 
                    opt.value.includes(val)
                )
            );
            
            if (matchedOptions.length > 0) {
                setSelectedOptions(matchedOptions);
                if (matchedOptions.length === 1 && includedValues.length === 1) {
                    setLastRightClickedOption(matchedOptions[0]);
                } else {
                    setLastRightClickedOption(null);
                }
            } else {
                setSelectedOptions([]);
                setLastRightClickedOption(null);
            }
        } else if (value.includes(' AND ')) {
            const excludedValues = value.split(' AND ').map(item => {
                return item.replace(/^NOT \(/, '').replace(/\)$/, '');
            });

            const includedOptions = options.filter(option => !excludedValues.includes(option.value));
            setSelectedOptions(includedOptions);
            setLastRightClickedOption(null);
        } else {
            setSelectedOptions([]);
            setLastRightClickedOption(null);
        }

    }, [value, options]);

    const filteredOptions = options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSelect = option => {
        let newSelectedOptions;

        if (lastRightClickedOption) {
            setLastRightClickedOption(null);
        }

        if (selectedOptions.some(selected => selected.value === option.value)) {
            newSelectedOptions = selectedOptions.filter(o => o.value !== option.value);
        } else {
            newSelectedOptions = [...selectedOptions, option];
        }

        // If user deselects all, treat as "Any" (select all tags)
        if (newSelectedOptions.length === 0) {
            setSelectedOptions(options);
            setLastRightClickedOption(null);
            handleChange(id, options, 'Any');
            return;
        }

        setSelectedOptions(newSelectedOptions);
        
        const excludedOptions = options.filter(opt => !newSelectedOptions.some(selected => selected.value === opt.value));
        const stateData = {
            included: newSelectedOptions,
            excluded: excludedOptions
        };
        
        handleChange(id, stateData);
    };


    const handleExclusiveSelect = (e, option) => {
        e.stopPropagation();

        if (lastRightClickedOption && lastRightClickedOption.value === option.value) {
            setSelectedOptions(options);
            setLastRightClickedOption(null);
            handleChange(id, options, 'Any');
        } else {
            setSelectedOptions([option]);
            setLastRightClickedOption(option);

            const includedValue = option.value;
            const allOtherOptions = options.filter(opt => opt.value !== option.value);

            const excludedTags = allOtherOptions.map(opt => {
                const match = opt.value.match(/\?'([^']+)'/);
                return match ? `'${match[1]}'` : null;
            }).filter(Boolean);

            const excludedArray = `NOT (dtm_summary->'dtm_summary_meta'->'tags' ? ANY(ARRAY[${excludedTags.join(', ')}]::text[]))`;
            const value = `(${includedValue}) AND ${excludedArray}`;

            handleChange(id, [option], value);
        }
    };

    const handleCheckboxClick = (e, option) => {
        e.stopPropagation();
        handleSelect(option);
    };
    return (
        <EnhancedCheckboxListWrapper>
            {label && (
                <StyledLabel>
                    {label}:
                </StyledLabel>
            )}

            <DropdownHeader onClick={() => setIsOpen(!isOpen)} isOpen={isOpen}>
                <span>{selectedOptions.length} of {options.length} selected</span>
                <DropdownChevron isOpen={isOpen} viewBox="0 0 12 8" aria-hidden="true">
                    <polyline points="1,1 6,6 11,1" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </DropdownChevron>
            </DropdownHeader>

            {isOpen && (
                <>
                    {enableSearch && (
                        <SearchInput
                            type="text"
                            placeholder="Search event types..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    )}

                    <ScrollableContainer maxHeight={maxHeight}>
                        {filteredOptions.sort((a, b) => {
                            const aIsSelected = selectedOptions.some(o => o.value === a.value);
                            const bIsSelected = selectedOptions.some(o => o.value === b.value);
                            
                            if (aIsSelected && !bIsSelected) return -1;
                            if (!aIsSelected && bIsSelected) return 1;
                            
                            if (a.label < b.label) return -1;
                            if (a.label > b.label) return 1;
                            return 0;
                        }).map(option => (
                            <CheckboxItem
                                key={option.value}
                                screener={true}
                                checked={selectedOptions.some(o => o.value === option.value)}
                                onClick={(e) => handleExclusiveSelect(e, option)}
                                onMouseEnter={() => setHoveredOption(option.value)}
                                onMouseLeave={() => setHoveredOption(null)}
                                title={showTooltips ? "Click checkbox to toggle. Click label to select only this option (click again to select all)." : undefined}
                                style={{ cursor: 'pointer' }}
                            >
                                <input
                                    type="checkbox"
                                    id={option.value}
                                    checked={selectedOptions.some(o => o.value === option.value)}
                                    onClick={(e) => handleCheckboxClick(e, option)}
                                    onMouseEnter={() => setIsHoveringCheckbox(true)}
                                    onMouseLeave={() => setIsHoveringCheckbox(false)}
                                    readOnly
                                    style={{ cursor: 'pointer' }}
                                />
                                <CheckboxLabel>
                                    {hoveredOption === option.value && !isHoveringCheckbox && "(Show Only) "}
                                    {option.label}
                                </CheckboxLabel>
                            </CheckboxItem>
                        ))}
                    </ScrollableContainer>
                </>
            )}
        </EnhancedCheckboxListWrapper>
    );
};

export default EnhancedCheckboxList;